import { html, LitElement } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { ApplicantsService } from '../../services/applicants.service.js';
import { DebugService } from '../../shared/services/debug.service.js';
import { SessionService } from '../../core/services/session/session.service.js';
import { phoneNumberFormatter } from '../../shared/formatters/phoneNumber.formatter.js';
import { dobFormatter } from '../../shared/formatters/dob.formatter.js';
import './personal-edit/personal-edit.component.js';
import './personal-review/personal-review.component.js';
import { stratum } from '../../../constants/stratum.js';
import { ApplicationService } from '../../services/application.service.js';
import { FeatureToggleService } from '../../shared/services/feature-toggle.service.js';

class RefiPersonalCheckout extends LitElement {
  constructor() {
    super();
    this.debug = new DebugService('RefiPersonalCheckout');
    this.eAppId = '';
    this.applicantId = '';
    this.applicant = {};
    this.updatedApplicant = {};
    this.loading = false;
    this.editBinded = this.onEdit.bind(this);

    this.checkout = 'review';
  }

  static properties = {
    isEdit: { type: Boolean },
    applicant: { type: Object },
    updatedApplicant: { type: Object },
    applicantId: { type: String },
    checkout: { type: String, attribute: 'checkout-page' },
    experiments: {
      type: Object,
      hasChanged: (newVal, oldVal) => {
        if (newVal !== oldVal) {
          // loop through this.experiments and add the object name to experimentIds array if it's eligible
          const experimentIds = [];
          Object.keys(newVal).forEach((experiment) => {
            if (newVal[experiment].isEligible) {
              experimentIds.push(newVal[experiment].allocation);
            }
          });
          ApplicationService.updateExperiments(experimentIds);
          FeatureToggleService.updateIsIconicAndToggledOn(false);
          return true;
        }
        return false;
      },
    },
  };

  connectedCallback() {
    super.connectedCallback();
    this.eAppId = SessionService.session?.eAppId;
    window.addEventListener('personal-edit', this.editBinded);
    this.loadApplicant();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('personal-edit', this.editBinded);
  }

  render() {
    if (this.checkout === 'review') {
      return html`<refi-personal-review
        .firstName="${this.applicant.firstName}"
        .lastName="${this.applicant.lastName}"
        .dob="${this.applicant.dateOfBirth}"
        .lastFourSsn="${this.applicant.lastFourSsn}"
        .phoneNumber="${this.applicant.phoneNumber}"
        .emailAddress="${this.applicant.emailAddress}"
      ></refi-personal-review>`;
    }
    if (this.checkout === 'edit') {
      return html`<refi-personal-edit
        .eAppId="${this.eAppId}"
        .applicantId="${this.applicantId}"
        .applicant="${this.applicant}"
        .updatedApplicant="${this.updatedApplicant}"
        @cancel-personal-edit="${this.cancelEdit}"
        @dispatch-unsaved-missing-fields="${this
          .dispatchUnsavedOrMissingFieldsEvent}"
        @personal-edit-success="${this.editSuccess}"
        @personal-edit-fail="${this.dispatchFailedToLoadOrUpdateEvent}"
      ></refi-personal-edit>`;
    }
    return html``;
  }

  dispatchCompleteLoadingEvent() {
    this.dispatchEvent(
      new CustomEvent('checkout-completed-loading', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'personal-card',
        },
      })
    );
    this.debug.log('completed loading event fired');
  }

  dispatchFailedToLoadOrUpdateEvent() {
    this.dispatchEvent(
      new CustomEvent('failed-load-or-update', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'personal-card',
        },
      })
    );
    this.checkout = 'error';
    this.debug.log('failed to load or update event fired');
  }

  dispatchUnsavedOrMissingFieldsEvent(ev) {
    // action unsaved, missing
    this.dispatchEvent(
      new CustomEvent('unsaved-missing', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: ev.detail.actionName,
          id: 'personal-card',
        },
      })
    );
    this.debug.log(`unsaved or missing fields event: ${ev.detail.actionName}`);
  }

  async loadApplicant() {
    try {
      this.loading = true;
      await ApplicantsService.getApplicantInformation(this.eAppId).then(
        (response) => {
          const primaryApplicant = response.applicants.find(
            (applicant) => applicant.applicantType === 'Applicant'
          );
          this.applicantId = primaryApplicant.applicantId;
          this.applicant = {
            firstName: primaryApplicant.firstName,
            lastName: primaryApplicant.lastName,
            dateOfBirth: dobFormatter(primaryApplicant.dateOfBirth),
            lastFourSsn: primaryApplicant.lastFourSsn,
            phoneNumber: phoneNumberFormatter(primaryApplicant.phoneNumber),
            phoneType: primaryApplicant.primaryPhoneType,
            hasTcpaConsent: primaryApplicant.hasTcpaConsent,
            emailAddress: primaryApplicant.emailAddress,
          };
          this.firePrefilledFieldTag(this.applicant);

          this.updatedApplicant = JSON.parse(JSON.stringify(this.applicant));
          this.loading = false;
          this.dispatchCompleteLoadingEvent();
        }
      );
      stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
        replacements: {
          value: 'personal mfe - successful',
        },
      });
    } catch (err) {
      this.debug.log(
        'Failed to retrieve personal information for review: ',
        err.message
      );
      stratum.publishTag(TagKey.PERSONAL_CHECKOUT_PERSONAL_MFE_LOAD, {
        replacements: {
          value: 'personal mfe - failed',
        },
      });
      this.dispatchFailedToLoadOrUpdateEvent();
    }
  }

  firePrefilledFieldTag(applicant) {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in applicant) {
      // eslint-disable-next-line no-prototype-builtins
      if (applicant.hasOwnProperty(key)) {
        const value = applicant[key];
        if (
          value !== '' &&
          value !== null &&
          value !== undefined &&
          value !== 0
        ) {
          stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, {
            replacements: {
              value: key === 'lastFourSsn' ? `iconic-ssn` : `iconic-${key}`,
            },
          });
        }
      }
    }
  }

  cancelEdit() {
    this.updatedApplicant = JSON.parse(JSON.stringify(this.applicant));
    // this.checkout = 'review';
    this.dispatchCloseEvent('cancel');
    this.isEdit = false;
  }

  editSuccess() {
    this.applicant = JSON.parse(JSON.stringify(this.updatedApplicant));
    // this.checkout = 'review';
    this.dispatchCloseEvent('update');
    this.isEdit = false;
  }

  onEdit() {
    this.checkout = 'edit';
    this.isEdit = true;
  }

  dispatchCloseEvent(action) {
    // Action is update or cancel
    stratum.publishTag(
      TagKey.PERSONAL_CHECKOUT_PERSONAL_EDIT_COMPONENT_COLLAPSED
    );
    this.dispatchEvent(
      new CustomEvent('close-editing', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: action,
          id: 'personal-card',
        },
      })
    );
  }
}

customElements.define('refi-personal-checkout', RefiPersonalCheckout);
